import {
  AgentTeamResultSchema,
  type AgentTeamContent,
} from '@littlebird/action-cards';
import { type ActionCard, HttpError, useStores } from '@littlebird/sdk';
import { observer } from 'mobx-react-lite';
import { useMemo, useState, type ReactNode } from 'react';

import { XStack, YStack } from '@/components/ui';
import { Button } from '@/components/ui/Button';
import { Checkbox } from '@/components/ui/Checkbox';
import { Label } from '@/components/ui/Label';
import { pluralize } from '@/lib/date-utils';
import { cn } from '@/lib/utils';
import { navigate } from '@/utils/navigation-service';

import { AgentAvatar } from './agents-avatar';
import { birdLookFromName } from './agents-bird.look';

const STATUS_LINE: Partial<Record<ActionCard['status'], string>> = {
  completed: 'Team added',
  dismissed: 'Skipped for now',
  revoked: 'Replaced by an updated proposal',
};

const useAgentTeamCard = (card: ActionCard, content: AgentTeamContent) => {
  const { agents, actionCards } = useStores();
  const [selectedIds, setSelectedIds] = useState(() =>
    content.agents.filter((agent) => agent.selected).map((agent) => agent.id)
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const run = async (action: () => Promise<unknown>) => {
    if (busy) return;
    setBusy(true);
    setError(undefined);
    try {
      await action();
    } catch (cause) {
      setError(
        cause instanceof HttpError && cause.userErrorMessage
          ? cause.userErrorMessage
          : 'Couldn’t update your team. Review the latest proposal and try again.'
      );
    } finally {
      setBusy(false);
    }
  };
  return {
    selectedIds,
    busy,
    error,
    toggle: (id: string, checked: boolean) =>
      setSelectedIds((ids) =>
        checked ? [...ids, id] : ids.filter((selected) => selected !== id)
      ),
    accept: () =>
      run(() => agents.acceptTeamProposal(card.id, selectedIds, card.syncId)),
    dismiss: () => run(() => actionCards.dismiss(card.id)),
  };
};

export const AgentTeamCard = observer(
  ({
    card,
    content,
    sourceChatTag,
  }: {
    card: ActionCard;
    content: AgentTeamContent;
    sourceChatTag?: ReactNode;
  }) => {
    const { selectedIds, busy, error, toggle, accept, dismiss } =
      useAgentTeamCard(card, content);
    const pending = card.status === 'draft';
    const completed = card.status === 'completed';
    const addedByOption = useMemo(
      () =>
        new Map(
          (AgentTeamResultSchema.safeParse(card.result).data?.agents ?? []).map(
            (created) => [created.option_id, created]
          )
        ),
      [card.result]
    );
    const selectedCount = selectedIds.length;
    return (
      <YStack
        className="w-full max-w-lg overflow-hidden rounded-2xl border border-border-strong bg-surface"
        aria-label="Your starter team"
        aria-busy={busy}
      >
        <XStack className="items-start justify-between gap-2 px-5 py-4">
          <YStack className="min-w-0 gap-1">
            <span className="font-medium text-foreground">
              Your starter team
            </span>
            <span className="text-sm text-foreground-muted" aria-live="polite">
              {STATUS_LINE[card.status] ??
                'Pick who to add. You can change any of this later.'}
            </span>
          </YStack>
          {sourceChatTag}
        </XStack>
        <YStack className="gap-1 border-t border-border-subtle px-2 py-2">
          {content.agents.map((agent) => {
            const added = addedByOption.get(agent.id);
            if (completed && !added) return null;
            const checkboxId = `${card.id}-${agent.id}`;
            const Row = pending ? Label : XStack;
            const selected = selectedIds.includes(agent.id);
            return (
              <Row
                key={agent.id}
                htmlFor={pending ? checkboxId : undefined}
                className={cn(
                  'flex items-center gap-3 rounded-xl py-3 pl-3 pr-4 text-[length:inherit] leading-[inherit] font-normal',
                  pending && !busy && !selected && 'hover:bg-surface-hover',
                  pending && selected && 'bg-surface-active'
                )}
              >
                <AgentAvatar
                  bird={birdLookFromName(agent.name)}
                  className="size-8"
                />
                <YStack className="min-w-0 flex-1 gap-0.5">
                  <span className="font-medium text-foreground">
                    {agent.name}
                  </span>
                  <span className="text-sm text-foreground-muted">
                    {agent.description}
                  </span>
                  {agent.routines.map((routine) => (
                    <span
                      key={routine.title}
                      className="text-xs text-foreground-muted"
                    >
                      {routine.title} · {routine.schedule}
                    </span>
                  ))}
                </YStack>
                {added && (
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() =>
                      navigate({
                        to: '/chats/$chatId',
                        params: { chatId: added.chat_id },
                      })
                    }
                  >
                    Open
                  </Button>
                )}
                {pending && (
                  <Checkbox
                    id={checkboxId}
                    className="size-[18px] rounded-md border-border data-[state=checked]:border-accent [&_svg]:size-3.5"
                    aria-label={`Add ${agent.name}`}
                    checked={selected}
                    onCheckedChange={(checked) =>
                      toggle(agent.id, checked === true)
                    }
                    disabled={busy}
                  />
                )}
              </Row>
            );
          })}
        </YStack>
        {pending && (
          <YStack className="gap-2 border-t border-border-subtle p-3">
            <XStack className="flex-wrap items-center justify-between gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => void dismiss()}
                disabled={busy}
              >
                Not now
              </Button>
              <Button
                size="sm"
                onClick={() => void accept()}
                disabled={busy || selectedCount === 0}
              >
                {busy
                  ? 'Saving…'
                  : `Add ${selectedCount} ${pluralize(selectedCount, 'agent')}`}
              </Button>
            </XStack>
            {error && (
              <span role="alert" className="text-sm text-destructive">
                {error}
              </span>
            )}
          </YStack>
        )}
      </YStack>
    );
  }
);
